import React, { useCallback, useEffect } from 'react';
import { RouteComponentProps } from 'react-router';
import { Switch, Route } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { usePageContext, useWindowSizeContext } from '@tradingblock/components';
import { useStateSelector } from '../data/global/dataSelectors';
import { UseAccountApiProvider } from '../context/Api';
import { DashboardView } from './views/DashboardView';
import { BaseCashieringView } from './views/BaseCashieringView';
import { SessionWrapper } from './wrappers/SessionWrapper';
import { usePageSettings } from '../framework/WrappedRoute';
import { AccountSwitcher } from '../components/account/AccountSwitcher';
import { account } from '../data/global/selectors/accountSelectors';
import { useDispatcher } from '../data/global/hooks';
import { IsVirtualEnvironment } from '../data/global/selectors/environmentSelector';
import { origin } from '../data/global/selectors/originSelector';
import { PendingEntitlementModal } from './modals/PendingEntitlementModal';
import { UIActions } from '../data/global/actions';
import { ScheduledTransferView } from './views/cashiering/ScheduledTransferView';
import { GetUrlParams } from '@tradingblock/api';
import { EmailUpdate } from '../blocks/AccountManagement/modals/EmailUpdate';
import { PhoneUpdate } from '../blocks/AccountManagement/modals/PhoneUpdate';

export const RootContentPage: React.FC<RouteComponentProps<{ path: string }>> = ({ location }) => {
  usePageSettings({ name: 'Root content', access: 'public' });
  const token = useStateSelector(s => s.auth.uiApiToken);
  const isVirtual = useStateSelector(IsVirtualEnvironment);
  const originVal = useStateSelector(origin.origin);
  let group = originVal === 'mb' ? 'mb' : 'tb';
  const { dispatcher } = useDispatcher();
  const showAccountSwitcher = useStateSelector(account.showAccountSwitcher);
  const toggleAccountSwitcher = useCallback(() => {
    dispatcher.account.toggleAccountSwitcher();
  }, [dispatcher.account]);
  const isPendingMarketDataEntitlementPolicyUpdatePresent = useStateSelector(
    s =>
      s.account.profile.current &&
      s.account.profile.current.entitlement &&
      s.account.profile.current.entitlement.isPendingMarketDataEntitlementPolicyUpdatePresent
  );
  const showPendingEntitlementModal = useStateSelector(s => s.ui.dashboard.entitlements.showPendingEntitlementModal);
  const emailUpdateModalData = useStateSelector(s => s.ui.dashboard.emailUpdateModalData);
  const phoneUpdateModalData = useStateSelector(s => s.ui.dashboard.phoneUpdateModalData);
  const showEmailUpdateModal = emailUpdateModalData.show;
  const showPhoneUpdateModal = phoneUpdateModalData.show;
  const dispatch = useDispatch();
  const urlSearchParams = GetUrlParams(location.search);
  const showAccountUpdateModal: 'email' | 'phone' | null = urlSearchParams['showAccountUpdateModal'] || null;
  const accountHolderIndexString: '0' | '1' | null = urlSearchParams['accountHolderIndex'] || null;

  useEffect(() => {
    if (
      (
        accountHolderIndexString === '0' ||
        accountHolderIndexString === '1'
      ) &&
      (
        showAccountUpdateModal === 'email' ||
        showAccountUpdateModal === 'phone'
      )
    ) {
      const accountHolderIndexNum = parseInt(accountHolderIndexString);

      if (showAccountUpdateModal === 'email') {
        dispatch(UIActions.setEmailUpdateModalData({
          show: true,
          accountHolderIndex: accountHolderIndexNum
        }));
      } else if (showAccountUpdateModal === 'phone') {
        dispatch(UIActions.setPhoneUpdateModalData({
          show: true,
          accountHolderIndex: accountHolderIndexNum
        }));
      }
    }
  }, [showAccountUpdateModal, accountHolderIndexString]);

  useEffect(() => {
    if (isPendingMarketDataEntitlementPolicyUpdatePresent && showPendingEntitlementModal === null) {
      dispatch(UIActions.setShowPendingEntitlementModal({ showPendingEntitlementModal: true }));
    }
  }, [isPendingMarketDataEntitlementPolicyUpdatePresent]);

  if (!token) {
    return <span>Loading account info...</span>;
  }

  return (
    <useWindowSizeContext.Provider>
      <usePageContext.Provider>
        <SessionWrapper>
          <UseAccountApiProvider>
            <Switch>
              <Route path="/accounts/oauth" component={BaseCashieringView} />
              <Route path="/scheduled-transfer/:direction" component={ScheduledTransferView} />
              <Route path="/:route(withdraw|deposit|accounts)" component={BaseCashieringView} />
              <Route path="/:dashboardid([\w-]{36})?" exact component={DashboardView} />
            </Switch>
            {!isVirtual && <AccountSwitcher show={showAccountSwitcher} toggleModal={toggleAccountSwitcher} />}
            {!isVirtual && showPendingEntitlementModal && !showEmailUpdateModal && !showPhoneUpdateModal && (
              <PendingEntitlementModal
                show={showPendingEntitlementModal}
                toggleModal={() =>
                  dispatch(
                    UIActions.setShowPendingEntitlementModal({
                      showPendingEntitlementModal: !showPendingEntitlementModal,
                    })
                  )
                }
              />
            )}
            {!isVirtual && showEmailUpdateModal && (emailUpdateModalData.accountHolderIndex === 0 || emailUpdateModalData.accountHolderIndex === 1) && (
              <EmailUpdate
                show={showEmailUpdateModal}
                toggleModal={() => dispatch(UIActions.setEmailUpdateModalData({ show: false, accountHolderIndex: null }))}
                accountHolderIndex={emailUpdateModalData.accountHolderIndex}
              />
            )}
            {!isVirtual && showPhoneUpdateModal && (phoneUpdateModalData.accountHolderIndex === 0 || phoneUpdateModalData.accountHolderIndex === 1) && (
              <PhoneUpdate
                show={showPhoneUpdateModal}
                toggleModal={() => dispatch(UIActions.setPhoneUpdateModalData({ show: false, accountHolderIndex: null }))}
                accountHolderIndex={phoneUpdateModalData.accountHolderIndex}
              />
            )}
          </UseAccountApiProvider>
        </SessionWrapper>
      </usePageContext.Provider>
    </useWindowSizeContext.Provider>
  );
};
